/**
 * No mutation pauses silently (offline program plan step T4).
 *
 * TanStack Query's default `networkMode: 'online'` PAUSES a mutation while the
 * browser reports no network: the request is held in memory, the button can sit
 * on "Saving…" (or an optimistic change can show as done), and closing the app
 * loses it. Neither app's QueryClient sets another mode, so every online-only
 * action behaves that way offline.
 *
 * `withNeedsSignal(action, options)` makes an online-only mutation honest:
 *   - `networkMode: 'always'`, so it never pauses in memory;
 *   - before the caller's own `onMutate` (so before any optimistic change) and
 *     before any request, it refuses when the phone is offline, with
 *     `NeedsSignalError` ("Needs signal to …. Nothing was saved.");
 *   - that refusal is shown as ONE toast per action (a stable toast id, so a
 *     repeated or parallel refusal replaces rather than stacks) and does NOT run
 *     the caller's `onError` (there is nothing to roll back — `onMutate` never
 *     ran). Callers that toast from their own `catch` must skip
 *     `isNeedsSignalError(err)`.
 *
 * WHAT "OFFLINE" MEANS HERE (PR #5515 review, decided):
 *   - Default: the browser's own offline flag (`isDeviceOffline()`), the same
 *     signal the report Submit routes on. With signal but a STALLED link
 *     (connectionHealth's probe), the action is ALLOWED: `networkMode: 'always'`
 *     means it no longer pauses, so it either succeeds or fails with its own
 *     visible error. Refusing on a stall would block actions that would have
 *     succeeded — the stall probe only checks the Firestore host, and its flag
 *     can stick if the probe disabled itself (CSP).
 *   - `refuseWhenStalled: true`: paid AI calls only. There a request on a bad
 *     link spends money for a result the technician may never see, so a stall
 *     (`isEffectivelyOffline()`) also refuses.
 *
 * Use it only for actions that are NOT on a durable queue. Do not queue them
 * instead unless the server write is idempotent by id: a replay after
 * reconnect would otherwise double-apply.
 */
import type { UseMutationOptions } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isEffectivelyOffline } from './connectionHealth';
import { isDeviceOffline } from './offlineErrors';

export interface NeedsSignalOptions {
  /** Also refuse on a stalled link (paid AI calls). Default false. */
  refuseWhenStalled?: boolean;
  /**
   * Whether the action would have saved something. False for a pure download:
   * the message then omits "Nothing was saved." Default true.
   */
  savesSomething?: boolean;
}

export class NeedsSignalError extends Error {
  readonly action: string;
  /**
   * Refused in the browser before any request was made, so an email send that
   * throws this provably did not go out. Read by classifySendFailure
   * (lib/email/sendOutcome) so a composer never shows "may have been sent" and
   * locks Send for a tech who simply had no signal.
   */
  readonly details = { sendOutcome: 'not_sent' as const };

  constructor(action: string, savesSomething = true) {
    super(savesSomething ? `Needs signal to ${action}. Nothing was saved.` : `Needs signal to ${action}.`);
    this.name = 'NeedsSignalError';
    this.action = action;
  }
}

/** True when the phone counts as offline for this kind of action. */
export function isOfflineFor(refuseWhenStalled = false): boolean {
  return refuseWhenStalled ? isEffectivelyOffline() : isDeviceOffline();
}

/** Throw NeedsSignalError when the phone has no signal (see module doc). */
export function refuseWhenOffline(action: string, options: NeedsSignalOptions = {}): void {
  if (isOfflineFor(options.refuseWhenStalled)) {
    throw new NeedsSignalError(action, options.savesSomething ?? true);
  }
}

export function isNeedsSignalError(error: unknown): error is NeedsSignalError {
  return error instanceof NeedsSignalError;
}

/** The toast id a refusal for `action` is shown under (so a caller can replace it). */
export function needsSignalToastId(action: string): string {
  return `needs-signal:${action}`;
}

/** Show a refusal once per action: a repeat replaces the visible toast. */
export function toastNeedsSignal(error: NeedsSignalError): void {
  toast.error(error.message, { id: needsSignalToastId(error.action) });
}

/**
 * Mutation `meta` flags read by the technician app's global backstop
 * (client-tech/src/config/techQueryClient.ts). The backstop refuses, offline,
 * every mutation that does not carry one of these; the office app ignores them.
 *
 *  - `durable: true`: the action sits on a durable queue and must keep pausing /
 *    queueing offline. (As of T4b no tech-reachable durable path is a
 *    useMutation: the forms outbox, draft sync, photo queue and survey outbox all
 *    call Firebase directly. The flag exists so a future one can opt in.)
 *  - `needsSignal: true`: the mutation refuses offline itself, with its own
 *    wording (set by `withNeedsSignal`, and by the briefing hooks).
 *
 * The Save Draft / Submit server writes (useCreateFormSubmission,
 * useUpdateFormSubmission, useSubmitWorkflowStage) carry NO flag since Nick's
 * ruling A (2026-09-29): FormFiller handles no-signal before reaching them
 * (Save Draft keeps the draft on the phone, Submit queues to the forms outbox),
 * and the backstop refuses them if signal drops in between.
 */
export interface OfflineMutationMeta extends Record<string, unknown> {
  durable?: boolean;
  needsSignal?: boolean;
}

export const NEEDS_SIGNAL_META: OfflineMutationMeta = { needsSignal: true };

/** True when the tech app's backstop must leave this mutation alone. */
export function isExemptFromOfflineBackstop(meta: Record<string, unknown> | undefined): boolean {
  return meta?.durable === true || meta?.needsSignal === true;
}

/** The action wording the backstop uses for a mutation that names none. */
export const BACKSTOP_ACTION = 'save this change';

export function withNeedsSignal<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  action: string,
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  signal: NeedsSignalOptions = {},
): UseMutationOptions<TData, TError, TVariables, TContext> {
  const { onMutate, onError } = options;
  return {
    ...options,
    meta: { ...options.meta, ...NEEDS_SIGNAL_META },
    networkMode: 'always',
    onMutate: async (variables, mutationContext) => {
      refuseWhenOffline(action, signal);
      return onMutate ? onMutate(variables, mutationContext) : (undefined as TContext);
    },
    onError: (error, variables, context, mutationContext) => {
      if (isNeedsSignalError(error)) {
        toastNeedsSignal(error);
        return;
      }
      return onError?.(error, variables, context, mutationContext);
    },
  };
}
